/* eslint-disable react/prop-types */
import { Box, ButtonProps, Paper, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PotentialInventoryColumnsDefs from './PotentialInventoryColumnDefs';
import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridColDef,
  GridDataGroupNode,
  gridFilteredDescendantCountLookupSelector,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
  GridRowParams,
  GridRowsProp,
  useGridApiContext,
  useGridApiRef,
  useGridSelector
} from '@mui/x-data-grid-pro';
import { GridGroupNode, GridRowSelectionModel } from '@mui/x-data-grid';
import { Account, Product, Supplier } from '../../../types';
import { getProductSubject } from '../../../store/Product';
import { getSupplierSubject } from '../../../store/Supplier';
import { FileNumberRenderer } from './FileNumber';
import { Icon } from '../../Atoms/Icon';
import { DD_GRID_CELL_SLOT_PROPS } from '../../../utils/telemetry';

export interface PotentialInventory {
  customerName?: string;
  customerName_confidence?: number;
  customerName_original?: string;
  customerName_explanation?: string;
  customerId?: string;
  customerId_confidence?: number;
  customerId_original?: string;
  customerId_explanation?: string;
  externalId?: string;
  externalId_confidence?: number;
  externalId_original?: string;
  externalId_explanation?: string;
  locationCode?: string;
  locationCode_confidence?: number;
  locationCode_original?: string;
  locationCode_explanation?: string;
  address?: string;
  address_confidence?: number;
  address_original?: string;
  address_explanation?: string;
  productFamily?: string;
  productFamily_original?: string;
  productName?: string;
  productName_confidence?: number;
  productName_original?: string;
  productName_explanation?: string;
  productId?: string;
  productId_confidence?: number;
  productId_original?: string;
  productId_explanation?: string;
  product?: Product; // Full product object for easier access
  supplierName?: string;
  supplierName_confidence?: number;
  supplierName_original?: string;
  supplierName_explanation?: string;
  supplierId?: string;
  supplierId_confidence?: number;
  supplierId_original?: string;
  supplierId_explanation?: string;
  supplier?: Supplier; // Full supplier object for easier access
  accessProvider?: string;
  accessProvider_confidence?: number;
  accessProvider_original?: string;
  accessProvider_explanation?: string;
  status?: string;
  status_confidence?: number;
  status_original?: string;
  status_explanation?: string;
  mRC?: number;
  mRC_confidence?: number;
  mRC_original?: number;
  mRC_explanation?: string;
  mRCWithoutCharges?: number;
  mRCWithoutCharges_confidence?: number;
  mRCWithoutCharges_original?: number;
  mRCWithoutCharges_explanation?: string;
  nRC?: number;
  nRC_confidence?: number;
  nRC_original?: number;
  nRC_explanation?: string;
  term?: string;
  term_confidence?: number;
  term_original?: string;
  term_explanation?: string;
  completionDate?: string;
  completionDate_confidence?: number;
  completionDate_original?: string;
  completionDate_explanation?: string;
  serviceActivationDate?: string;
  serviceActivationDate_confidence?: number;
  serviceActivationDate_original?: string;
  serviceActivationDate_explanation?: string;
  contractEndDate?: string;
  contractEndDate_confidence?: number;
  contractEndDate_original?: string;
  contractEndDate_explanation?: string;
  providerAccountNumber?: string;
  providerAccountNumber_confidence?: number;
  providerAccountNumber_original?: string;
  providerAccountNumber_explanation?: string;
  supplierOrderNumber?: string;
  supplierOrderNumber_confidence?: number;
  supplierOrderNumber_original?: string;
  supplierOrderNumber_explanation?: string;
  disconnectedReason?: string;
  disconnectedReason_confidence?: number;
  disconnectedReason_original?: string;
  disconnectedReason_explanation?: string;
  bandwidth?: string;
  bandwidth_confidence?: number;
  bandwidth_original?: string;
  bandwidth_explanation?: string;
  accessSpeed?: string;
  accessSpeed_confidence?: number;
  accessSpeed_original?: string;
  accessSpeed_explanation?: string;
  portSpeed?: string;
  portSpeed_confidence?: number;
  portSpeed_original?: string;
  portSpeed_explanation?: string;
  speedValue?: string;
  speedValue_confidence?: number;
  speedValue_original?: string;
  speedValue_explanation?: string;
  speedUnit?: 'kbps' | 'Mbps' | 'Gbps' | '';
  speedUnit_confidence?: number;
  speedUnit_original?: 'kbps' | 'Mbps' | 'Gbps' | '';
  speedUnit_explanation?: string;
  circuitIDs?: string;
  circuitIDs_confidence?: number;
  circuitIDs_original?: string;
  circuitIDs_explanation?: string;
  lecCircuitID?: string;
  lecCircuitID_confidence?: number;
  lecCircuitID_original?: string;
  lecCircuitID_explanation?: string;
  iPDetails?: string;
  iPDetails_confidence?: number;
  iPDetails_original?: string;
  iPDetails_explanation?: string;
  rackType?: string;
  rackType_confidence?: number;
  rackType_original?: string;
  rackType_explanation?: string;
  kwRack?: string;
  kwRack_confidence?: number;
  kwRack_original?: string;
  kwRack_explanation?: string;
  locationALecCircuitId?: string;
  locationALecCircuitId_confidence?: number;
  locationALecCircuitId_original?: string;
  locationALecCircuitId_explanation?: string;
  locationALecDemarc?: string;
  locationALecDemarc_confidence?: number;
  locationALecDemarc_original?: string;
  locationALecDemarc_explanation?: string;
  locationALocalLec?: string;
  locationALocalLec_confidence?: number;
  locationALocalLec_original?: string;
  locationALocalLec_explanation?: string;
  locationAText?: string;
  locationAText_confidence?: number;
  locationAText_original?: string;
  locationAText_explanation?: string;
  locationZLecCircuitId?: string;
  locationZLecCircuitId_confidence?: number;
  locationZLecCircuitId_original?: string;
  locationZLecCircuitId_explanation?: string;
  locationZLecDemarc?: string;
  locationZLecDemarc_confidence?: number;
  locationZLecDemarc_original?: string;
  locationZLecDemarc_explanation?: string;
  locationZLocalLec?: string;
  locationZLocalLec_confidence?: number;
  locationZLocalLec_original?: string;
  locationZLocalLec_explanation?: string;
  locationZText?: string;
  locationZText_confidence?: number;
  locationZText_original?: string;
  locationZText_explanation?: string;
  equipmentManufacturer?: string;
  equipmentManufacturer_confidence?: number;
  equipmentManufacturer_original?: string;
  equipmentManufacturer_explanation?: string;
  disconnectedDate?: string;
  disconnectedDate_confidence?: number;
  disconnectedDate_original?: string;
  disconnectedDate_explanation?: string;
  lecDemarc?: string;
  lecDemarc_confidence?: number;
  lecDemarc_original?: string;
  lecDemarc_explanation?: string;
  enclosure?: string;
  enclosure_confidence?: number;
  enclosure_original?: string;
  enclosure_explanation?: string;
  floor?: string;
  floor_confidence?: number;
  floor_original?: string;
  floor_explanation?: string;
  focDate?: string;
  focDate_confidence?: number;
  focDate_original?: string;
  focDate_explanation?: string;
  licenseQuantity?: number;
  licenseQuantity_confidence?: number;
  licenseQuantity_original?: number;
  licenseQuantity_explanation?: string;
  licenseExpirationDate?: string;
  licenseExpirationDate_confidence?: number;
  licenseExpirationDate_original?: string;
  licenseExpirationDate_explanation?: string;
  metered?: string;
  metered_confidence?: number;
  metered_original?: string;
  metered_explanation?: string;
  model?: string;
  model_confidence?: number;
  model_original?: string;
  model_explanation?: string;
  mount?: string;
  mount_confidence?: number;
  mount_original?: string;
  mount_explanation?: string;
  networkType?: string;
  networkType_confidence?: number;
  networkType_original?: string;
  networkType_explanation?: string;
  pduPhase?: string;
  pduPhase_confidence?: number;
  pduPhase_original?: string;
  pduPhase_explanation?: string;
  powerType?: string;
  powerType_confidence?: number;
  powerType_original?: string;
  powerType_explanation?: string;
  downloadSpeedMbps?: number;
  downloadSpeedMbps_confidence?: number;
  downloadSpeedMbps_original?: number;
  downloadSpeedMbps_explanation?: string;
  uploadSpeedMbps?: number;
  uploadSpeedMbps_confidence?: number;
  uploadSpeedMbps_original?: number;
  uploadSpeedMbps_explanation?: string;
  circuitRole?: string;
  circuitRole_confidence?: number;
  circuitRole_original?: string;
  circuitRole_explanation?: string;
  renewalLanguage?: string;
  renewalLanguage_confidence?: number;
  renewalLanguage_original?: string;
  renewalLanguage_explanation?: string;
  voiceType?: string;
  voiceType_confidence?: number;
  voiceType_original?: string;
  voiceType_explanation?: string;
  supplierSupportURL?: string;
  supplierSupportURL_confidence?: number;
  supplierSupportURL_original?: string;
  supplierSupportURL_explanation?: string;
  networkDetails?: string;
  networkDetails_confidence?: number;
  networkDetails_original?: string;
  networkDetails_explanation?: string;
  deviceDetails?: string;
  deviceDetails_confidence?: number;
  deviceDetails_original?: string;
  deviceDetails_explanation?: string;
  productSupportNumber?: string;
  productSupportNumber_confidence?: number;
  productSupportNumber_original?: string;
  productSupportNumber_explanation?: string;
  supplierSupportNumber?: string;
  supplierSupportNumber_confidence?: number;
  supplierSupportNumber_original?: string;
  supplierSupportNumber_explanation?: string;
  customerLocationName?: string;
  customerLabelOther?: string;
  fileNumber?: number;
  extraCharges?: PotentialInventoryCharge[];
  type?: 'outside_inventory' | 'line_item';
  name?: string;
  fileId?: string;
  tempId?: string;
}

export interface PotentialInventoryCharge {
  id: string;
  name: string;
  name_confidence?: number;
  name_explanation?: string;
  mRC: number;
  mRC_confidence?: number;
  mRC_explanation?: string;
  fileNumber?: number;
  externalId?: string;
  nRC: number;
  nRC_confidence?: number;
  nRC_explanation?: string | null;
  customerName?: string;
  customerId?: string;
  supplierName?: string;
  supplierId?: string;
  productId?: string;
  productName?: string;
  parentTempId?: string;
}

interface PotentialInventoryTableProps {
  inventoryItems: PotentialInventory[];
  onSelectedRowsChange?: (selectedRows: GridRowModel[]) => void;
  handleReviewCountUpdate?: (reviewCount: number) => void;
  accounts?: Account[];
  products?: Product[];
  suppliers?: Supplier[];
  showMRCWithoutCharges?: boolean;
}

const CustomGroupCellRenderer = (props: GridRenderCellParams) => {
  const { id, field, rowNode } = props;
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
  const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleClick: ButtonProps['onClick'] = (event) => {
    if (rowNode.type !== 'group') {
      return;
    }

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  return (
    <Box sx={{ ml: rowNode.depth * 4 }}>
      <div className="px-2 py-4 flex flex-col justify-start align-middle items-center">
        {filteredDescendantCount > 0 ? (
          <button
            onClick={handleClick}
            tabIndex={-1}
            className="flex flex-row gap-1 align-middle items-start">
            <Icon
              type="triangle"
              className={`p-1 ${(rowNode as GridDataGroupNode).childrenExpanded ? 'transform rotate-90' : ''}`}
            />
            <FileNumberRenderer fileNumber={props.row.fileNumber as number} />
          </button>
        ) : props.row.type === 'outside_inventory' ? (
          <FileNumberRenderer fileNumber={props.row.fileNumber as number} />
        ) : (
          <span />
        )}
      </div>
    </Box>
  );
};

// Helper component to display data in a table format grouped by location
const PotentialInventoryTable: React.FC<PotentialInventoryTableProps> = ({
  inventoryItems,
  onSelectedRowsChange,
  handleReviewCountUpdate,
  accounts = [],
  products: propProducts = [],
  suppliers: propSuppliers = [],
  showMRCWithoutCharges = false
}) => {
  const apiRef = useGridApiRef();
  const [allProducts, setAllProducts] = useState<Product[]>(propProducts);
  const [allSuppliers, setAllSuppliers] = useState<Supplier[]>(propSuppliers);
  const [localRowData, setLocalRowData] = useState<GridRowsProp>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (row) => row.path;
  const memoizedRows = useMemo(() => localRowData, [localRowData]);
  const userExpandedGroups = useRef(new Set());

  const groupRowDef = (): GridColDef => {
    return {
      field: 'fileNumber',
      headerName: 'File Number',
      flex: 1,
      editable: false,
      minWidth: 120,
      valueGetter: (params) => '',
      renderCell: (params) => <CustomGroupCellRenderer {...params} />
    };
  };

  useEffect(() => {
    if (!apiRef.current) return;

    return apiRef.current.subscribeEvent('rowExpansionChange', (node) => {
      if (node.childrenExpanded) {
        userExpandedGroups.current.add(node.id);
      } else {
        userExpandedGroups.current.delete(node.id);
      }
    });
  }, []);

  const isGroupExpandedByDefault = useCallback((node: GridGroupNode) => {
    // If user manually expanded it, respect that
    if (userExpandedGroups.current.has(node.id)) {
      return true;
    }
    // Otherwise, collapse by default
    return false;
  }, []);

  // Subscribe to products if not passed via props
  useEffect(() => {
    if (propProducts.length === 0) {
      const productSub = getProductSubject().subscribe((products) => setAllProducts(products));
      return () => {
        if (productSub) productSub.unsubscribe();
      };
    }
  }, [propProducts]);

  // Subscribe to suppliers if not passed via props
  useEffect(() => {
    if (propSuppliers.length === 0) {
      const supplierSub = getSupplierSubject().subscribe((suppliers) => setAllSuppliers(suppliers));
      return () => {
        if (supplierSub) supplierSub.unsubscribe();
      };
    }
  }, [propSuppliers]);

  // Helper function to find product by name or id
  const findProductMatch = (productName?: string, productId?: string): Product | null => {
    if (!productName && !productId) return null;

    // First try to find by exact ID match
    if (productId) {
      const productById = allProducts.find((p) => p.id === productId);
      if (productById) return productById;
    }

    // Then try to find by exact name match
    if (productName) {
      const productByName = allProducts.find((p) => p.name === productName);
      if (productByName) return productByName;
    }

    // Finally try fuzzy match on name (case insensitive, partial match)
    if (productName) {
      const productByFuzzyName = allProducts.find(
        (p) =>
          p.name.toLowerCase().includes(productName.toLowerCase()) ||
          productName.toLowerCase().includes(p.name.toLowerCase())
      );
      if (productByFuzzyName) return productByFuzzyName;
    }

    return null;
  };

  // Helper function to find supplier by name or id
  const findSupplierMatch = (supplierName?: string, supplierId?: string): Supplier | null => {
    if (!supplierName && !supplierId) return null;

    // First try to find by exact ID match
    if (supplierId) {
      const supplierById = allSuppliers.find((s) => s.id === supplierId);
      if (supplierById) return supplierById;
    }

    // Then try to find by exact name match
    if (supplierName) {
      const supplierByName = allSuppliers.find((s) => s.name === supplierName);
      if (supplierByName) return supplierByName;
    }

    // Finally try fuzzy match on name (case insensitive, partial match)
    if (supplierName) {
      const supplierByFuzzyName = allSuppliers.find(
        (s) =>
          s.name.toLowerCase().includes(supplierName.toLowerCase()) ||
          supplierName.toLowerCase().includes(s.name.toLowerCase())
      );
      if (supplierByFuzzyName) return supplierByFuzzyName;
    }

    return null;
  };

  // Function to initialize _original fields for data integrity and reset functionality
  const initializeOriginalFields = (item: PotentialInventory): PotentialInventory => {
    const initializedItem = { ...item };

    // Handle status field with default value
    const validStatusValues = ['Active', 'Installed', 'Pending Install', 'Disconnected', 'Cancelled', 'In Progress'];
    if (!initializedItem.status || !validStatusValues.includes(initializedItem.status)) {
      initializedItem.status = 'Disconnected'; // Default value
    }

    // Try to find and set complete customer information if selectedAccount exists
    if (accounts.length > 0) {
      const selectedAccount = accounts[0]; // Assuming first account is selected, or you might have a different logic
      if (!item.customerName || !item.customerId) {
        initializedItem.customerName = selectedAccount.name;
        initializedItem.customerId = selectedAccount.id;
      }
    }

    // Try to find and set complete product information
    const matchedProduct = findProductMatch(item.productName, item.productId);
    if (matchedProduct) {
      // Set both productName and productId if we found a match
      initializedItem.productName = matchedProduct.name;
      initializedItem.productId = matchedProduct.id;
      initializedItem.productFamily = matchedProduct.family;
      // Store the full product object for use in other parts of the application
      (initializedItem as Record<string, unknown>).product = matchedProduct;
    }

    // Try to find and set complete supplier information
    const matchedSupplier = findSupplierMatch(item.supplierName, item.supplierId);
    if (matchedSupplier) {
      // Set both supplierName and supplierId if we found a match
      initializedItem.supplierName = matchedSupplier.name;
      initializedItem.supplierId = matchedSupplier.id;
      // Store the full supplier object for use in other parts of the application
      (initializedItem as Record<string, unknown>).supplier = matchedSupplier;
    }
    if (!initializedItem.term) initializedItem.term = 'Month to Month';

    // List of all field names (without _confidence, _original, _explanation suffixes)
    const fieldNames = [
      'customerName',
      'customerId',
      'externalId',
      'locationCode',
      'address',
      'productName',
      'productFamily',
      'productId',
      'supplierName',
      'supplierId',
      'accessProvider',
      'status',
      'mRC',
      'mRCWithoutCharges',
      'nRC',
      'term',
      'completionDate',
      'serviceActivationDate',
      'contractEndDate',
      'providerAccountNumber',
      'supplierOrderNumber',
      'disconnectedReason',
      'bandwidth',
      'accessSpeed',
      'portSpeed',
      'speedValue',
      'speedUnit',
      'circuitIDs',
      'lecCircuitID',
      'iPDetails',
      'rackType',
      'kwRack',
      'locationALecCircuitId',
      'locationALecDemarc',
      'locationALocalLec',
      'locationAText',
      'locationZLecCircuitId',
      'locationZLecDemarc',
      'locationZLocalLec',
      'locationZText',
      'equipmentManufacturer',
      'disconnectedDate',
      'lecDemarc',
      'enclosure',
      'floor',
      'focDate',
      'licenseQuantity',
      'licenseExpirationDate',
      'metered',
      'model',
      'mount',
      'networkType',
      'pduPhase',
      'powerType',
      'downloadSpeedMbps',
      'uploadSpeedMbps',
      'circuitRole',
      'renewalLanguage',
      'voiceType',
      'supplierSupportURL',
      'networkDetails',
      'deviceDetails',
      'productSupportNumber',
      'supplierSupportNumber'
    ];

    // Initialize _original fields if they don't exist
    fieldNames.forEach((fieldName) => {
      const originalFieldName = `${fieldName}_original` as keyof PotentialInventory;
      const currentValue = initializedItem[fieldName as keyof PotentialInventory];
      if (initializedItem[originalFieldName] === undefined && currentValue !== undefined) {
        (initializedItem as Record<string, unknown>)[originalFieldName] = currentValue;
      }
    });

    return initializedItem;
  };

  // Initialize local row data when the source data changes or accounts/products/suppliers are loaded
  useEffect(() => {
    const initialRows = inventoryItems.flatMap((item: PotentialInventory) => {
      const rows = [];
      rows.push({
        id: item.tempId,
        path: [item.tempId],
        type: 'outside_inventory',
        ...initializeOriginalFields(item)
      });
      if (item.extraCharges && Array.isArray(item.extraCharges) && item.extraCharges.length > 0) {
        const matchedProduct = findProductMatch(item.productName, item.productId);
        rows.push(
          ...item.extraCharges.map((charge) => ({
            ...charge,
            path: [charge.parentTempId, charge.id],
            type: 'line_item',
            ...(matchedProduct && {
              productName: matchedProduct.name,
              productId: matchedProduct.id,
              productFamily: matchedProduct.family
            })
          }))
        );
      }
      return rows;
    });
    setLocalRowData(initialRows);
  }, [inventoryItems, accounts.length, allProducts.length, allSuppliers.length]);

  useEffect(() => {
    setReviewCount(
      localRowData.reduce((count, row) => {
        const confidenceFields = [
          'customerName_confidence',
          'customerId_confidence',
          'externalId_confidence',
          'locationCode_confidence',
          'address_confidence',
          'productName_confidence',
          'productId_confidence',
          'supplierName_confidence',
          'supplierId_confidence',
          'accessProvider_confidence',
          'status_confidence',
          'mRC_confidence',
          'mRCWithoutCharges_confidence',
          'nRC_confidence',
          'term_confidence',
          'completionDate_confidence',
          'serviceActivationDate_confidence',
          'contractEndDate_confidence',
          'providerAccountNumber_confidence',
          'supplierOrderNumber_confidence',
          'disconnectedReason_confidence',
          'bandwidth_confidence',
          'accessSpeed_confidence',
          'portSpeed_confidence',
          'speedValue_confidence',
          'speedUnit_confidence',
          'circuitIDs_confidence',
          'lecCircuitID_confidence',
          'iPDetails_confidence',
          'rackType_confidence',
          'kwRack_confidence',
          'locationALecCircuitId_confidence',
          'locationALecDemarc_confidence',
          'locationALocalLec_confidence',
          'locationAText_confidence',
          'locationZLecCircuitId_confidence',
          'locationZLecDemarc_confidence',
          'locationZLocalLec_confidence',
          'locationZText_confidence',
          'equipmentManufacturer_confidence',
          'disconnectedDate_confidence',
          'lecDemarc_confidence',
          'enclosure_confidence',
          'floor_confidence',
          'focDate_confidence',
          'licenseQuantity_confidence',
          'licenseExpirationDate_confidence',
          'metered_confidence',
          'model_confidence',
          'mount_confidence',
          'networkType_confidence',
          'pduPhase_confidence',
          'powerType_confidence',
          'downloadSpeedMbps_confidence',
          'uploadSpeedMbps_confidence',
          'circuitRole_confidence',
          'renewalLanguage_confidence',
          'voiceType_confidence',
          'supplierSupportURL_confidence',
          'networkDetails_confidence',
          'deviceDetails_confidence',
          'productSupportNumber_confidence',
          'supplierSupportNumber_confidence'
        ];
        return (
          count +
          confidenceFields.reduce((fieldCount, field) => {
            return fieldCount + (row[field] !== undefined && row[field] < 0.95 ? 1 : 0);
          }, 0)
        );
      }, 0)
    );
  }, [localRowData]);

  useEffect(() => {
    if (handleReviewCountUpdate) handleReviewCountUpdate(reviewCount);
  }, [reviewCount, handleReviewCountUpdate]);

  const indeterminateGroups = useMemo(() => {
    if (!apiRef.current) return new Set();

    const tree = apiRef.current.state?.rows?.tree || {};
    const indeterminate = new Set();
    const selectionSet = new Set(rowSelectionModel);

    Object.keys(tree).forEach((id) => {
      const node = tree[id];
      if (node?.type === 'group') {
        const leafChildren = node.children?.filter((childId) => tree[childId]?.type === 'leaf') || [];

        const selectedCount = leafChildren.filter((childId) => selectionSet.has(childId)).length;

        if (selectedCount > 0 && selectedCount < leafChildren.length) {
          indeterminate.add(id);
        }
      }
    });

    return indeterminate;
  }, [rowSelectionModel, localRowData]);

  // Handle selection change
  const handleSelectionModelChange = (gridSelectedRows: GridRowSelectionModel, details: GridCallbackDetails) => {
    const selectedRows: GridRowModel[] = gridSelectedRows
      .map((id) => {
        const row = localRowData.find((r) => r.id === id);
        return row as GridRowModel;
      })
      .filter((row): row is GridRowModel => row !== undefined);

    const selectedRowsWithChildren: GridRowModel[] = selectedRows.reduce((allSelectedRows: GridRowModel[], row) => {
      // Include child line items if parent is selected
      if (
        row.type === 'outside_inventory' &&
        !rowSelectionModel.includes(row.id) &&
        row.extraCharges &&
        Array.isArray(row.extraCharges)
      ) {
        const childLineItems = localRowData.filter((r) => r.type === 'line_item' && r.parentTempId === row.tempId);
        /**
         * Only add all child line items if they are not already included
         * This prevents duplicates when both parent and some children are selected
         * as well as when child items are selected/unselected individually
         */
        if (
          childLineItems.length > 0 &&
          childLineItems.every((child) => !selectedRows.some((selectedRow) => selectedRow.id === child.id))
        ) {
          allSelectedRows.push(...childLineItems);
        }
      }

      // Add parent row if line item has been selected and parent was not just removed
      const parentRow = localRowData.find((r) => r.type === 'outside_inventory' && r.tempId === row.parentTempId);
      if (
        parentRow &&
        row.type === 'line_item' &&
        !selectedRows.some((selectedRow) => selectedRow.tempId === parentRow.tempId) &&
        !rowSelectionModel.includes(parentRow.id) &&
        !selectedRows.some((selectedRow) => selectedRow.id !== row.id && selectedRow.parentTempId === row.parentTempId)
      ) {
        allSelectedRows.push(...[parentRow, row]);
      }

      // Do not add line items if parent is not selected
      if (row.type === 'line_item' && !selectedRows.some((selectedRow) => selectedRow.tempId === row.parentTempId)) {
        return allSelectedRows;
      }

      allSelectedRows.push(row);

      return allSelectedRows;
    }, [] as GridRowModel[]);
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedRowsWithChildren);
    }
    setRowSelectionModel(selectedRowsWithChildren.map((row) => row.id));
  };

  function handleCellReset(row: GridRowModel) {
    // Update local row data when cell is reset
    setLocalRowData((prevRows) => prevRows.map((prevRow) => (prevRow.id === row.id ? row : prevRow)));

    if (onSelectedRowsChange) {
      const selectedRows: GridRowModel[] = Array.from(apiRef.current?.getSelectedRows().values() || []);
      onSelectedRowsChange(selectedRows);
    }
  }

  function handleRowUpdate(newRow: GridRowModel, oldRow: GridRowModel, params: { rowId: GridRowId }) {
    try {
      // Update local row data to preserve edits
      setLocalRowData((prevRows) => prevRows.map((row) => (row.id === params.rowId ? newRow : row)));

      if (onSelectedRowsChange) {
        let selectedRows = Array.from(apiRef.current?.getSelectedRows().values() || []);

        // Check if the edited row is still selectable
        // For handleRowUpdate, we use the helper function directly on the row data
        const isStillSelectable = isRowDataSelectable(newRow);

        if (!isStillSelectable) {
          // Remove the row from selection if it's no longer selectable
          const currentSelection = Array.from(apiRef.current?.getSelectedRows().keys() || []);
          const updatedSelectionModel = currentSelection.filter((id: GridRowId) => id !== params.rowId);

          // Update the grid's selection model
          setRowSelectionModel(updatedSelectionModel);

          // Update selectedRows to reflect the removal
          selectedRows = selectedRows.filter((row) => row.id !== params.rowId);
        } else {
          // Update the row data in selectedRows if it's still selected
          selectedRows = selectedRows.map((row) => {
            if (row.id === params.rowId) {
              return newRow;
            }
            return row;
          });
        }

        onSelectedRowsChange(selectedRows);
      }
      return newRow;
    } catch (error) {
      console.error('Error in handleRowUpdate:', error);
      // Return the old row to prevent data corruption
      return oldRow;
    }
  }

  // Helper function to check if row data meets selectability requirements
  function isRowDataSelectable(rowData: PotentialInventory & { id?: number | string }): boolean {
    // Required fields: product id, customer id, supplier id, and address
    return rowData.type === 'outside_inventory'
      ? !!(
          rowData.productId &&
          rowData.customerId &&
          rowData.supplierName &&
          rowData.address &&
          rowData.externalId &&
          rowData.term
        )
      : !!(rowData.name && rowData.mRC !== undefined);
  }

  function isRowSelectable(params: GridRowParams): boolean {
    // In isRowSelectable, the GridRowModel has a nested structure with row data inside params.row
    return isRowDataSelectable(params.row);
  }

  return (
    <Paper
      elevation={0}
      sx={{ p: 0, mb: 3 }}>
      <Box sx={{ flexDirection: 'column', display: 'flex' }}>
        {localRowData ? (
          <Box sx={{ height: '38rem', width: '100%' }}>
            <DataGridPro
              treeData
              slotProps={{ cell: DD_GRID_CELL_SLOT_PROPS }}
              getTreeDataPath={getTreeDataPath}
              groupingColDef={groupRowDef()}
              defaultGroupingExpansionDepth={0}
              isGroupExpandedByDefault={isGroupExpandedByDefault}
              rows={memoizedRows}
              hideFooter={true}
              isRowSelectable={isRowSelectable}
              columns={PotentialInventoryColumnsDefs(
                handleCellReset,
                accounts,
                allProducts,
                allSuppliers,
                showMRCWithoutCharges
              )}
              checkboxSelection={true}
              processRowUpdate={handleRowUpdate}
              isCellEditable={(params) => {
                if (
                  params.row.type === 'line_item' &&
                  !['externalId', 'mRC', 'nRC', 'productName'].includes(params.field)
                )
                  return false;
                return true;
              }}
              onProcessRowUpdateError={(error) => {
                console.error('Row update error:', error);
                return error;
              }}
              disableRowSelectionOnClick={true}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleSelectionModelChange}
              apiRef={apiRef}
              pagination={false}
              getCellClassName={(params) => {
                if (params.field === '__check__' && indeterminateGroups.has(params.id)) {
                  return 'checkbox-indeterminate';
                }
                return '';
              }}
              sx={{
                '& .MuiDataGrid-cell': {
                  border: '.5px solid #e0e0e0', // Add grid lines
                  px: 0.5,
                  '.MuiInputBase-root, .MuiFormControl-root': { height: '100%' },
                  '.MuiBox-root': { padding: '0' },
                  '.MuiPickersOutlinedInput-notchedOutline': { border: 'none' }
                },
                '& .MuiDataGrid-columnSeparator > svg > rect': {
                  height: '100%' // Add grid lines
                },
                '& .checkbox-indeterminate .MuiCheckbox-root': {
                  '& .MuiSvgIcon-root': {
                    // Hide the default icon
                    display: 'none'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '18px',
                    height: '18px',
                    backgroundColor: 'primary.main', // Blue box
                    borderRadius: '2px',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: '10px',
                    height: '2px',
                    backgroundColor: '#fff', // White dash
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1
                  }
                }
              }}
            />
          </Box>
        ) : (
          <Typography variant="body1">No table data available or data format not supported.</Typography>
        )}
      </Box>
    </Paper>
  );
};

export default PotentialInventoryTable;
